import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCarouselAdvertisements, trackAdvertisementEvent } from "../../api/advertisementService";
import "./clientTheme.css";
import "./AdCarousel.css";

const AUTO_ADVANCE_MS = 5000;

export default function AdCarousel() {
  const { t } = useTranslation("adCarousel");
  const [ads, setAds] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef(null);
  const viewedRef = useRef(new Set());
  const videoRefs = useRef({});

  useEffect(() => {
    getCarouselAdvertisements()
      .then(setAds)
      .catch(() => setAds([]));
  }, []);

  useEffect(() => {
    if (ads.length <= 1) return undefined;

    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % ads.length);
    }, AUTO_ADVANCE_MS);

    return () => clearInterval(timerRef.current);
  }, [ads]);

  // A non-looping video plays once each time its slide comes into view.
  useEffect(() => {
    const ad = ads[activeIndex];
    const video = ad && videoRefs.current[ad.advertisementId];
    if (!video || ad.videoLoop !== false) return;

    video.currentTime = 0;
    video.play().catch(() => {});
  }, [ads, activeIndex]);

  useEffect(() => {
    const ad = ads[activeIndex];
    if (!ad || viewedRef.current.has(ad.advertisementId)) return;

    viewedRef.current.add(ad.advertisementId);
    trackAdvertisementEvent(ad.advertisementId, "view");
  }, [ads, activeIndex]);

  if (ads.length === 0) return null;

  function handleAdClick(ad) {
    trackAdvertisementEvent(ad.advertisementId, "click");
    if (ad.websiteLinkUrl) {
      window.open(ad.websiteLinkUrl, "_blank", "noopener,noreferrer");
    }
  }

  return (
    <div className="ad-carousel">
      <div
        className="ad-carousel-track"
        style={{ transform: `translateX(-${activeIndex * 100}%)` }}
      >
        {ads.map((ad) => (
          <div
            key={ad.advertisementId}
            className={`ad-carousel-slide${ad.websiteLinkUrl ? " ad-carousel-slide-clickable" : ""}`}
            onClick={() => handleAdClick(ad)}
          >
            {ad.videoUrl ? (
              <video
                ref={(el) => {
                  videoRefs.current[ad.advertisementId] = el;
                }}
                src={ad.videoUrl}
                className="ad-carousel-image"
                autoPlay
                muted
                loop={ad.videoLoop !== false}
                playsInline
              />
            ) : (
              <img src={ad.imageUrl} alt={ad.title} className="ad-carousel-image" />
            )}
          </div>
        ))}
      </div>

      {ads.length > 1 && (
        <div className="ad-carousel-dots">
          {ads.map((ad, index) => (
            <button
              key={ad.advertisementId}
              type="button"
              className={`ad-carousel-dot${index === activeIndex ? " ad-carousel-dot-active" : ""}`}
              aria-label={t("showSlide", { number: index + 1 })}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
